import { GuidePriority } from "../guide";
import type { GuideContext, GuideScript, GuideStep, GuideTopicId, LevelFacts } from "../guide";
import { featureLevelSteps } from "./features";

/**
 * Captain Bram's contextual hints in battles (DESIGN §6.4): each shows once per save, at the moment it matters (the
 * first flyer on screen, the first upgrade card in a hand). None is part of the walkthrough. "Captain's tips" off
 * silences them all except the warnings about real threats (priority Safety: the first boss, flyers, bombers, the last
 * stand), which always show once (owner, 2026-09-24). The cards that stop the action (boss, flyers, new enemies, the
 * last stand) hold the sim while up.
 *
 * Steps key on facts that stay true while the thing is on screen (an enemy alive, a card in the hand), not on the
 * one-shot `enemy:<id>` / `tower:<id>` signals: a step queued behind a wave banner must still be wanted when the
 * banner goes.
 */

/** The step-id prefix of the new-enemy hints: the rest is the enemy's archetype id (level/guideLevel.ts reads it for `newEnemy`). */
export const NEW_ENEMY_STEP_PREFIX = "hint.enemy.";

const L = (c: GuideContext): LevelFacts | null => c.level;
const H = { screen: "level" } as const;
/** From this level on the in-battle extras (rally, SKIP, the map) are taught; level 1 has its walkthrough. */
const ExtrasFromLevel = 2;

/**
 * The enemies that get a card of their own the first time one is on the road. `threat`: a warning (priority Safety,
 * shown even with tips off): bombers are the one enemy that attacks the defence itself, knocking towers out. The
 * others make raiders tougher, which is advice rather than a danger the player cannot see coming.
 */
const NEW_ENEMIES: readonly { readonly id: string; readonly topic: GuideTopicId; readonly text: string; readonly threat?: true }[] = [
  { id: "shieldbearer", topic: "shieldbearers", text: "**Shieldbearers** guard the raiders around them. Break them first, Commander." },
  { id: "healer", topic: "healers", text: "**Healers** patch up their friends. Knock them out first." },
  { id: "bomber", topic: "bombers", text: "**Bombers**! They knock towers out for a few seconds. Spread your towers out.", threat: true },
  // M21 (docs/ENEMIES_V2.md), in unlock order. The Stunner is a warning: like the bomber it knocks towers out.
  { id: "summoner", topic: "summoners", text: "A **Summoner**! It stops to call **imps**. Hit it hard while it casts, and splash the imps." },
  { id: "burrower", topic: "burrowers", text: "A **Burrower**! Hit it and it dives under the road, out of reach. **Soldiers** pin it down." },
  { id: "spellward", topic: "spellwards", text: "A **Spellward**! Its runes turn magic aside. Hit it with **arrows, cannons, ballistas and mortars**." },
  { id: "stunner", topic: "stunners", text: "A **Stunner**! Its stomp stuns towers near the road. Mind the **orange ring** and keep some towers back.", threat: true },
];

/** The towers that unlock after level 1 and get a line the first time their card is dealt. */
const NEW_TOWERS: readonly { readonly id: string; readonly text: string }[] = [
  { id: "fire", text: "New tower: **Fire**! It sets raiders alight, and the burn keeps biting." },
  { id: "lightning", text: "New tower: **Lightning**! Each bolt leaps to the raiders nearby." },
  { id: "ballista", text: "New tower: **Ballista**! Its bolts punch through a whole line of raiders." },
  { id: "poison", text: "New tower: **Poison**! It eats through raiders' armour." },
  { id: "beacon", text: "New tower: **Beacon**! It doesn't shoot. It makes nearby towers stronger." },
  // TOWERS_V2, in unlock order (Barracks L4, Arcane Spire L6, Mortar L7, Hex Totem L10)
  { id: "barracks", text: "New tower: **Barracks**! Its soldiers block the road, and raiders must fight their way past." },
  { id: "arcane", text: "New tower: **Arcane Spire**! The longer its beam holds one raider, the harder it burns." },
  { id: "mortar", text: "New tower: **Mortar**! It shells raiders far off, but can't hit anything close by." },
  { id: "hex", text: "New tower: **Hex Totem**! It doesn't shoot, but raiders near it take extra damage from everything." },
];

/** The TOWERS_V2 towers' card hints teach their own Guide topics (topics.ts, group "towers"). */
const TOWER_TOPIC: Readonly<Record<string, GuideTopicId>> = { barracks: "barracks", arcane: "arcane", mortar: "mortar", hex: "hex" };

/** An in-run offer is open with nothing picked. */
const offerOpen = (l: LevelFacts | null) => l !== null && l.offer === "inRun" && l.picked === null;
/** The offer the step was about has been answered (a card picked or the offer gone). */
const offerAnswered = (c: GuideContext) => { const l = L(c); return l === null || l.picked !== null || l.offer !== "inRun"; };

const steps: GuideStep[] = [
  { ...H, id: "hint.firstBoss", kind: "info", pose: "pointing", holdSim: true, priority: GuidePriority.Safety, topic: "bosses",
    target: { world: "boss", focus: true },
    text: "A **boss**! Big and tough. Keep every tower in range on it.",
    when: (c) => L(c)?.bossAlive === true,
    until: (c) => L(c)?.bossAlive !== true },
  { ...H, id: "hint.flyers", kind: "info", pose: "pointing", holdSim: true, priority: GuidePriority.Safety, topic: "flyers",
    target: { world: "firstFlyer", focus: true },
    text: (c) => {
      const names = L(c)?.airTowerNames ?? [];
      return names.length > 0
        ? `Flyers skip the road and come straight in! These towers can hit them: **${names.join(", ")}**.`
        : "Flyers skip the road and come straight in! Get an **Archer** or **Frost** tower on them.";
    },
    when: (c) => L(c)?.airEnemyAlive === true,
    until: (c) => L(c)?.airEnemyAlive !== true },
  ...NEW_ENEMIES.map((e): GuideStep => ({
    ...H, id: `${NEW_ENEMY_STEP_PREFIX}${e.id}`, kind: "info", pose: "pointing", holdSim: true, priority: e.threat ? GuidePriority.Safety : GuidePriority.NewThing, topic: e.topic,
    target: { world: "newEnemy" },
    text: e.text,
    when: (c) => L(c)?.aliveEnemyIds.includes(e.id) === true,
    until: (c) => L(c)?.aliveEnemyIds.includes(e.id) !== true,
  })),
  { ...H, id: "hint.lastStand", kind: "info", pose: "pointing", holdSim: true, priority: GuidePriority.Safety, topic: "soldiers",
    target: { dom: "#controls .flag" },
    text: "The fortress is under siege! Tap the **flag** and send the soldiers to block the road.",
    when: (c) => { const l = L(c); return l !== null && l.level >= ExtrasFromLevel && l.phase === "wave" && l.lastStand; },
    until: (c) => { const l = L(c); return l === null || l.phase !== "wave" || l.rallyArmed; } },
  // Level 1's walkthrough now teaches the flag (l1.rally); this is for a player who skipped it.
  { ...H, id: "hint.rally", kind: "tip", pose: "pointing", topic: "soldiers", unless: ["l1.rally"],
    target: { dom: "#controls .flag" }, block: "none",
    text: "Your soldiers guard the road by the fortress. Tap the **flag**, then the road, to move them.",
    when: (c) => { const l = L(c); return l !== null && l.level >= ExtrasFromLevel && l.phase === "wave" && l.troopsFighting; },
    until: (c) => c.signals.has("rallyArmed") || c.signals.has("rallyPlanted") || L(c)?.rallyArmed === true },
  { ...H, id: "hint.upgradeCard", kind: "info", pose: "pointing", priority: GuidePriority.NewThing, topic: "upgrades",
    target: { dom: "#hand .cards .card.upgrade" },
    text: "An **upgrade card** raises one tower a tier. Tap it, then the tower.",
    when: (c) => { const l = L(c); return offerOpen(l) && l!.offerCards.includes("upgrade"); },
    until: offerAnswered },
  // M55: the first Move card (dealt in a road slot once three towers stand).
  { ...H, id: "hint.moveCard", kind: "info", pose: "pointing", priority: GuidePriority.NewThing, topic: "upgrades",
    target: { dom: "#hand .cards .card.move" },
    text: "A **move card**! Tap it, then a tower, then a new square. The tower keeps its tier.",
    when: (c) => { const l = L(c); return offerOpen(l) && l!.offerCards.includes("move"); },
    until: offerAnswered },
  ...NEW_TOWERS.map((t): GuideStep => ({
    ...H, id: `hint.newTower.${t.id}`, kind: "info", pose: "pointing", priority: GuidePriority.NewThing, topic: TOWER_TOPIC[t.id] ?? "towers",
    target: { dom: `#hand .cards .card.t-${t.id}` },
    text: t.text,
    when: (c) => { const l = L(c); return l !== null && l.offer !== null && l.picked === null && l.offerTowerIds.includes(t.id); },
    until: (c) => { const l = L(c); return l === null || l.picked !== null || l.offer === null; },
  })),
  // The Mortar's blind spot, the moment it matters: a Mortar card in hand and a spot chosen, its red ring on the map.
  { ...H, id: "hint.mortarBlind", kind: "tip", pose: "pointing", priority: GuidePriority.NewThing, topic: "mortar", block: "none",
    target: { world: "towerSpot", radiusCells: 1.6 },
    text: "See the **red ring**? The Mortar can't hit raiders that close, so build it back from the road.",
    when: (c) => { const l = L(c); return l !== null && l.picked === "tower" && l.towerSpotChosen && l.pickedTowerId === "mortar"; },
    until: (c) => { const l = L(c); return l === null || l.picked !== "tower" || l.pickedTowerId !== "mortar"; } },
  // Tier IV (M21, docs/TOWERS_V2.md): the first Ascend card, the branch picker it opens, the first skill card.
  { ...H, id: "hint.ascendCard", kind: "info", pose: "pointing", priority: GuidePriority.NewThing, topic: "ascend",
    target: { dom: "#hand .card.special.ascend" },
    text: "An **Ascend** card! It lifts your tier-3 towers of that kind to **tier IV**. Tap it to choose how.",
    when: (c) => { const l = L(c); return offerOpen(l) && l!.offerCards.includes("ascend"); },
    until: offerAnswered },
  { ...H, id: "hint.branchPicker", kind: "tip", pose: "pointing", priority: GuidePriority.NewThing, topic: "ascend", block: "none",
    target: { dom: "#branch-picker .bcards" },
    text: "Two **branches**, two ways to fight. Each has **two skills** that come on later cards.",
    when: (c) => L(c)?.picked === "ascend",
    until: (c) => L(c)?.picked !== "ascend" },
  { ...H, id: "hint.skillCard", kind: "info", pose: "pointing", priority: GuidePriority.NewThing, topic: "ascend",
    target: { dom: "#hand .card.special.skill" },
    text: "A **skill card** teaches your tier-IV towers a new trick. Tap it, then **LEARN**.",
    when: (c) => { const l = L(c); return offerOpen(l) && l!.offerCards.includes("skill"); },
    until: offerAnswered },
  // M45 review: a tip, not a card the player must tap away before choosing (it blocked the offer it talks about)
  { ...H, id: "hint.reroll", kind: "tip", pose: "pointing", priority: GuidePriority.Tip, topic: "points",
    target: { dom: "#hand [data-guide=reroll]" }, block: "none",
    text: "Bad hand? **REROLL** deals fresh cards. You earn one every 5 waves.",
    when: (c) => { const l = L(c); return offerOpen(l) && l!.rerollCharges >= 1; },
    until: offerAnswered },
  { ...H, id: "hint.skip", kind: "tip", pose: "pointing", topic: "points",
    target: { dom: "#hand [data-guide=skip]" }, block: "none",
    text: "Nothing you need? **SKIP** gives bonus points toward your next hand.",
    when: (c) => { const l = L(c); return offerOpen(l) && l!.level >= ExtrasFromLevel && !l!.noSkipChallenge; },
    until: offerAnswered },
  // The turn arrows step aside while the card tray is up, so this waits for the board before START WAVES (DESIGN
  // §6.4 says "draft": the arrows are hidden for the whole draft).
  { ...H, id: "hint.mapControls", kind: "tip", pose: "neutral", topic: "map",
    target: { dom: "#rotate-map" }, block: "none",
    text: "Drag to look around, pinch to zoom, and use these arrows to turn the map.",
    when: (c) => { const l = L(c); return l !== null && l.level >= ExtrasFromLevel && l.phase === "ready"; },
    until: (c) => c.signals.has("mapMoved") },
];

export const levelHintsScript: GuideScript = { id: "levelHints", steps: [...steps, ...featureLevelSteps] };
